import React, { useEffect, useMemo } from 'react';
import { Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormProps, getRequiredValidationRule, getValidationErrorProps } from 'util/Form';
import { ApplicationQuestionOption } from 'store/types/ApplicationQuestion';
import { currencyQuestionDelimiter } from 'util/Application';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';

interface CurrencyQuestionFormItemProps extends FormProps {
  fieldName: string;
  options?: ApplicationQuestionOption[];
  required?: boolean;
}

const CurrencyQuestionFormItem: React.FunctionComponent<CurrencyQuestionFormItemProps> = ({
  fieldName,
  options = [],
  required,
  disabled,
}) => {
  const { control, watch, setValue, getValues } = useFormContext();

  const typeFieldName = `${fieldName}-type`;
  const amountFieldName = `${fieldName}-amount`;

  const fieldTypeValue = watch(typeFieldName);
  const fieldAmountValue = watch(amountFieldName);

  const optionItems = useMemo(() => {
    return (
      options?.map(({ name, value }) => (
        <MenuItem value={name} key={`${typeFieldName}-option-${name}-${value}`}>
          {name}
        </MenuItem>
      )) ?? []
    );
  }, [typeFieldName, options]);

  useEffect(() => {
    if (options.length > 0 && (fieldTypeValue || fieldAmountValue)) {
      const type = getValues(`${typeFieldName}`);
      const amount = getValues(`${amountFieldName}`);

      setValue(fieldName, `${type || ''}${amount ? `${currencyQuestionDelimiter}${amount}` : ''}`);
    }
  }, [
    amountFieldName,
    fieldAmountValue,
    fieldName,
    fieldTypeValue,
    getValues,
    options.length,
    setValue,
    typeFieldName,
  ]);

  return options.length > 0 ? (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps} md={6}>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              select={true}
              required={required || !!fieldAmountValue}
              label={'Currency'}
              {...getValidationErrorProps(error)}
              disabled={disabled}
            >
              {optionItems}
            </TextField>
          )}
          name={typeFieldName}
          defaultValue={''}
          control={control}
          rules={{
            required: getRequiredValidationRule('your answer', false, required || !!fieldAmountValue),
          }}
        />
      </Grid>
      <Grid {...defaultGridItemProps} md={6}>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              required={required || !!fieldTypeValue}
              {...getValidationErrorProps(error)}
              disabled={disabled}
              label={'Amount'}
              type={'number'}
              placeholder={'0'}
            />
          )}
          name={amountFieldName}
          defaultValue={''}
          control={control}
          rules={{
            required: getRequiredValidationRule('your answer', false, required || !!fieldTypeValue),
          }}
        />
      </Grid>
    </Grid>
  ) : (
    <Controller
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          required={required}
          {...getValidationErrorProps(error)}
          disabled={disabled}
          type={'number'}
          InputProps={{
            inputProps: { min: 0 },
            startAdornment: <InputAdornment position={'start'}>$</InputAdornment>,
          }}
          placeholder={'0'}
        />
      )}
      name={fieldName}
      control={control}
      rules={{
        required: getRequiredValidationRule('your answer', false, required),
      }}
    />
  );
};
export default CurrencyQuestionFormItem;
